// composables
import { computed, ref, type Ref } from 'vue'
// composables
import useApiRequest from '@/composables/useApiRequest'
// utilities
import { alertToast } from '@/utilities/notification'
import {
  buildCustomerVehiclePayload,
  buildVehicleDecodeString,
  formatMissingDecodedFields,
  missingRequiredDecodedFields
} from '@/utilities/vehicle/decodeVehicle'
// types
import type { Agreement } from '@/types/domain/agreement'
import type { DecodedVehicle, VehicleDecodeFailure } from '@/types/domain/customerVehicle'

/**
 * Reads a message from an API rejection.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message !== '') {
      return message
    }
  }

  return fallback
}

/**
 * Opens the decode modal, requests a decode, and saves a confirmed vehicle.
 */
export default function useAgreementVehicleDecode(
  agreementData: Ref<Agreement | null>
) {
  /** Whether the decode modal is open. */
  const decodeModalIsOpen = ref(false)
  /** Vehicle string sent to the decoder. */
  const vehicleString = ref('')
  /** Decoded vehicle shown in the modal. */
  const decodedVehicle = ref<DecodedVehicle | null>(null)
  /** Fields the decoder could not complete. */
  const missingFields = ref<string[]>([])
  /** Decode or save error shown in the modal. */
  const decodeError = ref<string | null>(null)

  /** Request used to decode the vehicle string. */
  const decodeRequest = useApiRequest()
  /** Request used to save the confirmed vehicle. */
  const saveRequest = useApiRequest()

  /**
   * Whether the agreement vehicle has a VIN that can be stored.
   */
  const hasVin = computed(() => {
    return (agreementData.value?.vehicle.vin ?? '').trim() !== ''
  })

  /**
   * Make, Model, or Trim values that are still empty.
   */
  const missingRequiredFields = computed(() => missingRequiredDecodedFields(decodedVehicle.value))

  /**
   * Whether the user can save the current decode.
   * Make, Model, and Trim must all be present.
   */
  const canSaveDecodedVehicle = computed(() => {
    return (
      decodeModalIsOpen.value &&
      !decodeRequest.isLoading.value &&
      !saveRequest.isLoading.value &&
      decodedVehicle.value !== null &&
      missingRequiredFields.value.length === 0 &&
      hasVin.value
    )
  })

  /**
   * Opens the modal and decodes the agreement vehicle string.
   */
  async function openDecodeVehicleModal(): Promise<void> {
    const agreement = agreementData.value
    // The page can still be loading when the action is triggered.
    if (!agreement) {
      return
    }

    vehicleString.value = buildVehicleDecodeString({
      make: agreement.vehicle.make,
      model: agreement.vehicle.model,
      derivative: agreement.vehicle.derivative,
      modelYear: agreement.vehicle.modelYear
    })
    decodedVehicle.value = null
    missingFields.value = []
    decodeError.value = null
    decodeModalIsOpen.value = true

    // There is nothing to send when the agreement has no vehicle text.
    if (vehicleString.value === '') {
      decodeError.value = 'This agreement does not have a vehicle description to decode.'
      return
    }

    try {
      const response = await decodeRequest.send<DecodedVehicle | VehicleDecodeFailure>({
        endpoint: '/v1/decoding/vehicle',
        method: 'POST',
        data: {
          vehicleString: vehicleString.value
        }
      })

      // A 400 response carries the partial decode and the reason it stopped.
      if (!response.success) {
        const failure = response.data as VehicleDecodeFailure | null
        const missing = failure?.missingFields ?? []
        decodedVehicle.value = failure?.decoded ?? null
        missingFields.value = missing
        // Name the missing fields instead of repeating the generic decoder message.
        decodeError.value = missing.length
          ? `Missing ${formatMissingDecodedFields(missing)}.`
          : (response.message ?? 'Unable to decode this vehicle')
        return
      }

      decodedVehicle.value = response.data as DecodedVehicle
    } catch (error) {
      decodeError.value = errorMessage(error, 'Unable to decode this vehicle')
    }
  }

  /**
   * Closes the decode modal.
   */
  function closeDecodeVehicleModal(): void {
    // Keep the modal open until the save request finishes.
    if (saveRequest.isLoading.value) {
      return
    }

    decodeModalIsOpen.value = false
  }

  /**
   * Saves the confirmed decode onto the customer vehicle record.
   */
  async function saveDecodedVehicle(): Promise<void> {
    const agreement = agreementData.value
    const decoded = decodedVehicle.value

    // Save only after a complete decode for an agreement that has a VIN.
    if (!agreement || !decoded || !canSaveDecodedVehicle.value) {
      return
    }

    try {
      const response = await saveRequest.send({
        endpoint: '/v1/customer-vehicles',
        method: 'POST',
        data: buildCustomerVehiclePayload({
          agreementId: agreement.id,
          customerId: agreement.customer.id,
          vin: agreement.vehicle.vin,
          vrm: agreement.vehicle.vrm,
          newUsed: agreement.newUsed,
          vehicleCategory: agreement.vehicle.category,
          isVehicleImported: agreement.vehicle.isImported,
          color: agreement.vehicle.color,
          previousOwners: agreement.vehicle.previousOwners,
          registrationDate: agreement.vehicle.regDate,
          sourceDescription: vehicleString.value,
          decoded,
          fallback: {
            make: agreement.vehicle.make,
            model: agreement.vehicle.model,
            derivative: agreement.vehicle.derivative,
            modelYear: agreement.vehicle.modelYear,
            doors: agreement.vehicle.doors,
            fuelType: agreement.vehicle.fuelType
          }
        })
      })

      // Keep the modal open so the user can see why the save was rejected.
      if (!response.success) {
        decodeError.value = response.message ?? 'Unable to save this vehicle'
        return
      }

      decodeModalIsOpen.value = false
      alertToast('Vehicle saved', null, 'success')
    } catch (error) {
      decodeError.value = errorMessage(error, 'Unable to save this vehicle')
    }
  }

  return {
    decodeModalIsOpen,
    vehicleString,
    decodedVehicle,
    missingFields,
    decodeError,
    hasVin,
    missingRequiredFields,
    canSaveDecodedVehicle,
    isDecoding: decodeRequest.isLoading,
    isSavingDecodedVehicle: saveRequest.isLoading,
    openDecodeVehicleModal,
    closeDecodeVehicleModal,
    saveDecodedVehicle
  }
}
